/* eslint-disable no-nested-ternary */
import * as React from 'react';
import {
  Box,
  Checkbox,
  createStyles,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  makeStyles,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import useSWR from 'swr';
import Skeleton from '@material-ui/lab/Skeleton';
import type { IBoxInformationProps, IStore } from './OpenerInterfaces';
import ImproperlyPackedDialog from './ImproperlyPackedDialog';
import ManifestMismatchDialog from './ManifestMismatchDialog';
import type { ICompany } from '../massReceive/MassReceiveInterfaces';

const useStyles = makeStyles(() => createStyles({
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
}));

const BoxInformation: React.FC<IBoxInformationProps> = (
  { openedBox, mutate: mutateProp },
) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [checkboxState, setCheckboxState] = React.useState({
    manifestEnclosed: openedBox ? openedBox.manifestEnclosed : false,
    manifestMatches: openedBox ? openedBox.manifestMatches : false,
    containsJewelry: openedBox ? openedBox.containsJewelry : false,
    improperlyPacked: openedBox ? openedBox.improperlyPacked : false,
  });
  const [companyLoading, setCompanyLoading] = React.useState(false);
  const [storeLoading, setStoreLoading] = React.useState(false);

  const [company, setCompany] = React.useState<ICompany | undefined>(openedBox?.company);
  const [stores, setStores] = React.useState<IStore[]>([]);
  const [store, setStore] = React.useState<IStore | undefined>(openedBox?.store);

  const fetcher = (url: string, boxId: string) => axios.get<{ companies: ICompany[], stores: IStore[] }>(url, {
    params: {
      boxId,
    },
  })
    .then((response) => response.data);

  const { data } = useSWR<{ companies: ICompany[], stores: IStore[] }>(
    ['/opener/getBoxInformation', openedBox.boxId],
    (url, boxIdParameter) => fetcher(url, boxIdParameter),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  const handleSelectedCompanyChange = (companyParam: ICompany | null) => {
    if (!companyParam) {
      enqueueSnackbar('Please select a valid store.', { persist: false, variant: 'error' });
    } else {
      setCompanyLoading(true);
      axios.get<IStore[]>('/opener/getstoresforcompany', {
        params: {
          companyId: companyParam.companyId,
        },
      })
        .then((response: { data: IStore[]; }) => {
          setCompany(companyParam);
          setStore(undefined);
          setStores(response.data);
          mutateProp().catch((error) => enqueueSnackbar(error, { persist: false, variant: 'error' }));
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        })
        .finally(() => setCompanyLoading(false));
    }
  };

  const handleSelectedStoreChange = (storeParam : IStore | null) => {
    if (!storeParam) {
      enqueueSnackbar('Please select a valid store.', { persist: false, variant: 'error' });
    } else {
      setStoreLoading(true);
      axios.post<void>('/opener/updateboxstore', {
        boxId: openedBox.boxId,
        storeId: storeParam.storeId,
      })
        .then(() => {
          setStore(storeParam);
          mutateProp().catch((error) => enqueueSnackbar(error, { persist: false, variant: 'error' }));
        })
        .catch((error: AxiosError) => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        })
        .finally(() => setStoreLoading(false));
    }
  };

  const handleManifestEnclosedCheckboxStateChange = (isManifestEnclosed: boolean) => {
    axios.post('/opener/updatemanifestenclosed', {
      boxId: openedBox.legacyBoxId,
      isManifestEnclosed,
    })
      .then(() => {
        enqueueSnackbar(`is manifest enclosed successfully set to ${isManifestEnclosed.toString()}`, { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        // reset state
        setCheckboxState({ ...checkboxState, manifestEnclosed: checkboxState.manifestEnclosed });
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const [showManifestMismatchDialog, setShowManifestMismatchDialog] = React.useState<boolean>(false);
  const handleManifestMismatchCheckboxStateChange = (manifestMatches: boolean) => {
    // if the manifest matches
    if (manifestMatches) {
      // clear details; no manager code required
      axios.post('/opener/updatemanifestmismatchdetails', {
        boxId: openedBox.legacyBoxId,
        manifestMismatchDetails: '',
      })
        .then(() => {
          enqueueSnackbar(`box isManifestReconciles set to ${manifestMatches.toString()}`, { variant: 'success' });
        })
        .catch((error: AxiosError) => {
          // don't reset state; dialog remains open until cancelled
          setCheckboxState({ ...checkboxState, manifestMatches: checkboxState.manifestMatches });
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    }

    // if the manifest doesn't match, details will be filled in dialog
    setShowManifestMismatchDialog(!manifestMatches);
  };

  const handleContainsJewelryCheckboxStateChange = (containsJewelry: boolean) => {
    axios.post('/opener/updatecontainsjewelry', {
      boxId: openedBox.legacyBoxId,
      containsJewelry,
    })
      .then(() => {
        enqueueSnackbar(`contains jewelry successfully set to ${containsJewelry.toString()}`, { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        // reset state
        setCheckboxState({ ...checkboxState, containsJewelry: checkboxState.containsJewelry });
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const [showImproperlyPackedDialog, setShowImproperlyPackedDialog] = React.useState<boolean>(false);
  const handleImproperlyPackedCheckboxStateChange = (improperlyPacked: boolean) => {
    // if box was not improperly packed
    if (!improperlyPacked) {
      // clear reasons
      axios.post('/opener/updateimproperlypackedreasons', {
        boxId: openedBox.legacyBoxId,
        improperlyPackedReasons: [],
      })
        .then(() => {
          enqueueSnackbar(`Box improperly packed successfully set to ${improperlyPacked.toString()}`, { variant: 'success' });
        })
        .catch((error: AxiosError) => {
          // reset state
          setCheckboxState({ ...checkboxState, improperlyPacked: checkboxState.improperlyPacked });
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    }

    // if box was improperly packed, reasons will be selected in dialog
    setShowImproperlyPackedDialog(improperlyPacked);
  };

  const handleCheckboxStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxState({ ...checkboxState, [event.target.name]: event.target.checked });
    switch (event.target.name) {
      case 'manifestEnclosed':
        handleManifestEnclosedCheckboxStateChange(event.target.checked);
        break;
      case 'manifestMatches':
        handleManifestMismatchCheckboxStateChange(event.target.checked);
        break;
      case 'containsJewelry':
        handleContainsJewelryCheckboxStateChange(event.target.checked);
        break;
      case 'improperlyPacked':
        handleImproperlyPackedCheckboxStateChange(event.target.checked);
        break;
      default:
        break;
    }
  };

  const handleManifestMismatchDialogClose = (details?: string, managerCode?: string) => {
    // if modal was closed with details to save
    if (details && managerCode) {
      // update details
      axios.post('/opener/updatemanifestmismatchdetails', {
        boxId: openedBox.legacyBoxId,
        manifestMismatchDetails: details,
        managerCode,
      })
        .then(() => {
          enqueueSnackbar(`box manifest does not match, with details: ${details}`, { variant: 'success' });
          setShowManifestMismatchDialog(false);
        })
        .catch((error: AxiosError) => {
          // don't reset state; dialog remains open until cancelled
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      // reset state
      setCheckboxState({ ...checkboxState, manifestMatches: !checkboxState.manifestMatches });
      setShowManifestMismatchDialog(false);
    }
  };
  const handleImproperlyPackedDialogClose = (reasons?: Array<string>) => {
    setShowImproperlyPackedDialog(false);

    if (reasons && reasons?.length > 0) {
      axios.post('/opener/updateimproperlypackedreasons', {
        boxId: openedBox.legacyBoxId,
        improperlyPackedReasons: reasons,
      })
        .then(() => {
          const improperlyPacked = reasons.length > 0;
          let message = `Box improperly packed successfully set to ${improperlyPacked.toString()}`;
          if (improperlyPacked) {
            message += ` for the following reasons: ${reasons.join(', ')}`;
          }
          enqueueSnackbar(message, { variant: 'success' });
        })
        .catch((error: AxiosError) => {
          // reset state
          setCheckboxState({ ...checkboxState, improperlyPacked: !checkboxState.improperlyPacked });
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
    } else {
      // reset state
      setCheckboxState({ ...checkboxState, improperlyPacked: !checkboxState.improperlyPacked });
    }
  };

  return (
    <Box>
      <Paper>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box pl={2}>
              <Typography className={classes.title}>{'Box Information'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <Box pl={2}>
              { !data
                ? <Skeleton><TextField fullWidth /></Skeleton>
                : (
                  openedBox.isUnopened
                    ? (
                      <Autocomplete
                        value={company}
                        onChange={(event: unknown, selectedCompany: ICompany | null) => {
                          handleSelectedCompanyChange(selectedCompany);
                        }}
                        filterOptions={(options, params) => options.filter((option) => option.companyName.toLowerCase().indexOf(params.inputValue.toLowerCase()) === 0)}
                        getOptionSelected={(option: ICompany, value: ICompany) => option?.companyId === value?.companyId}
                        autoHighlight
                        autoSelect
                        disabled={companyLoading}
                        options={data.companies.sort((a, b) => (a.companyName > b.companyName ? 1 : -1))}
                        getOptionLabel={(option) => option?.companyName?.toString()}
                        id="company-select"
                    // prop spreading is directly from MUI tsx example
                    // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => (<TextField autoFocus={company?.companyName === null} {...params} label="Company" variant="outlined" />)}
                      />
                    )
                    : (
                      <TextField variant="outlined" label="Company" fullWidth value={company?.companyName} />
                    )
                )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box pr={2}>
              { !data
                ? <Skeleton><TextField fullWidth /></Skeleton>
                : (
                  openedBox.isUnopened
                    ? (
                      <Autocomplete
                        value={store}
                        onChange={(event: unknown, selectedStore: IStore | null) => {
                          handleSelectedStoreChange(selectedStore);
                        }}
                        filterOptions={(options, params) => options.filter((option) => option.storeName.toLowerCase().indexOf(params.inputValue.toLowerCase()) === 0)}
                        getOptionSelected={(option: IStore, value: IStore) => (option?.storeId === value?.storeId)}
                        autoHighlight
                        autoSelect
                        disabled={storeLoading}
                        options={stores.length > 0 ? stores.sort((a, b) => (a.storeName > b.storeName ? 1 : -1)) : data.stores.sort((a, b) => (a.storeName > b.storeName ? 1 : -1))}
                        getOptionLabel={(option) => option.storeName}
                        id="store-select"
                // prop spreading is directly from MUI tsx example
                // eslint-disable-next-line react/jsx-props-no-spreading
                        renderInput={(params) => <TextField {...params} label="Store" variant="outlined" />}
                      />
                    )
                    : (
                      <TextField variant="outlined" label="Store" fullWidth value={store?.storeName} />
                    )
                )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box pl={2}>
              <FormGroup>
                <FormControlLabel
                  style={{ pointerEvents: 'none' }}
                  control={(
                    <Checkbox
                      id="manifest-enclosed-checkbox"
                      style={{ pointerEvents: 'auto' }}
                      color="primary"
                      name="manifestEnclosed"
                      checked={checkboxState.manifestEnclosed}
                      onChange={handleCheckboxStateChange}
                    />
                  )}
                  label="Is manifest enclosed?"
                />
                <FormControlLabel
                  style={{ pointerEvents: 'none' }}
                  control={(
                    <Checkbox
                      id="manifest-matches-checkbox"
                      style={{ pointerEvents: 'auto' }}
                      color="primary"
                      name="manifestMatches"
                      checked={checkboxState.manifestMatches}
                      onChange={handleCheckboxStateChange}
                    />
                  )}
                  label="Does manifest match?"
                />
                <FormControlLabel
                  style={{ pointerEvents: 'none' }}
                  control={(
                    <Checkbox
                      id="contains-jewelry-checkbox"
                      style={{ pointerEvents: 'auto' }}
                      color="primary"
                      name="containsJewelry"
                      checked={checkboxState.containsJewelry}
                      onChange={handleCheckboxStateChange}
                    />
                  )}
                  label="Contains Jewelry?"
                />
                {openedBox?.isMacys
                  && (
                    <FormControlLabel
                      style={{ pointerEvents: 'none' }}
                      control={(
                        <Checkbox
                          id="improperly-packed-checkbox"
                          style={{ pointerEvents: 'auto' }}
                          color="primary"
                          name="improperlyPacked"
                          checked={checkboxState.improperlyPacked}
                          onChange={handleCheckboxStateChange}
                        />
                      )}
                      label="Is Box Improperly Packed?"
                    />
                  )}
              </FormGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <ImproperlyPackedDialog
        trackingNumber={openedBox.trackingNumber}
        showModal={showImproperlyPackedDialog}
        onClose={handleImproperlyPackedDialogClose}
      />

      <ManifestMismatchDialog
        trackingNumber={openedBox.trackingNumber}
        showModal={showManifestMismatchDialog}
        onClose={handleManifestMismatchDialogClose}
      />

    </Box>
  );
};

export default BoxInformation;
